import { Button, Typography, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import Highcharts from 'highcharts';
import HC_patternFill from 'highcharts-pattern-fill';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRouteMatch } from 'react-router';

import ButtonGroup from 'components/button-group';
import CheckboxSelectItem from 'components/checkbox-select-item';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import LoadingSpinner from 'components/loading-spinner';
import TextField from 'components/text-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { Box, useMediaQuery } from '@mui/material';
import Select from 'components/select';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { getAverageTemp, getAverageTempYears, getTemperatureProfile, putTemperatureProfile } from '../../api/temperature-profile';
import { TemperatureProfile as TemperatureProfileProps } from '../../types/temperature-profile.type';
import AverageTemperatureChart from './average-temperature-chart';

type ParamTypes = {
  globalSiteId: string;
};

type FormValues = {
  profiles: TemperatureProfileProps[];
};

type Props = {};

HC_patternFill(Highcharts);

const PAGE_NAME_ID = 'siteSettings.editSiteSettings.temperatureProfile';

const defaultValues = new Array<TemperatureProfileProps>(53).fill({ weekNumber: 0, averageTemperature: 0 }).map((_, i) => ({
  weekNumber: i + 1,
  averageTemperature: 0,
}));

const TemperatureProfile: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { params } = useRouteMatch<ParamTypes>();
  const temperatureDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  //#region STATE
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [showTable, setShowTable] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  //#endregion

  //#region FORM

  const { handleSubmit, control, setValue } = useForm<FormValues>({
    defaultValues: {
      profiles: defaultValues,
    },
    shouldUnregister: false,
  });

  const { fields: fieldsTemperature } = useFieldArray({
    control: control,
    name: 'profiles',
    shouldUnregister: false,
  });

  const values = useWatch({
    control,
  });

  const validData =
    values?.profiles?.map((profile) => ({
      weekNumber: profile.weekNumber || 0,
      averageTemperature: profile.averageTemperature || 0,
    })) || [];
  //#endregion

  //#region FETCH DATA
  const { data: temperatureProfile } = useGet(
    ['getTemperatureProfile', params.globalSiteId],
    ({ queryKey }) => getTemperatureProfile(String(queryKey[1])),
    {
      enabled: !!params.globalSiteId,
    }
  );

  const { data: averageTempYears } = useGet(
    ['getAverageTempYears', params.globalSiteId],
    ({ queryKey }) => getAverageTempYears(String(queryKey[1])),
    {
      enabled: !!params.globalSiteId,
    }
  );
  //#endregion

  //#region POST DATA
  const fetchAverageTemp = usePost<TemperatureProfileProps[], number[]>(
    (request) => getAverageTemp(params.globalSiteId, request),
    (response) => {
      if (response.length > 1) setValue('profiles', response);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const updateTemperatureProfile = usePost<AxiosResponse, TemperatureProfileProps[]>(
    (request) => putTemperatureProfile(params.globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getTemperatureProfile', params.globalSiteId],
        exact: true
      });
      queryClient.invalidateQueries({
        queryKey: ['getSiteSettings']
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      temperatureDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion

  //#region HANDLERS
  const handleFetchAverageTemp = (years: number[]) => {
    fetchAverageTemp.mutate(years);
  };

  const handleClose = () => {
    setShowTable(false);
  };

  useEffect(() => {
    if (selectedYears.length > 0) {
      handleFetchAverageTemp(selectedYears);
    } else {
      if (!!temperatureProfile && temperatureProfile.length > 1) setValue('profiles', temperatureProfile);
    }
  }, [selectedYears, temperatureProfile, setValue]);

  const handleUpdateTemperatureProfile = (request: FormValues) => {
    updateTemperatureProfile.mutate(request.profiles);
  };

  const chartData = useMemo(() => {
    return (
      temperatureProfile?.map((profile) => ({
        weekNumber: profile.weekNumber || 0,
        averageTemperature: profile.averageTemperature || 0,
      })) || []
    );
  }, [temperatureProfile]);
  //#endregion

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" color={theme.palette.common.white}>
        <Typography variant="h3">
          <FormattedMessage id={`${PAGE_NAME_ID}.title`} />
        </Typography>
        <Dialog
          ref={temperatureDialogRef}
          title={intl.formatMessage({ id: `${PAGE_NAME_ID}.temperatureDialog.title` })}
          maxWidth="sm"
          onClose={handleClose}
          button={
            <Button size="small">
              <FormattedMessage id={`general.edit`} values={{ type: 'Temperature Profile' }} />
            </Button>
          }>
          <>
            <form onSubmit={handleSubmit(handleUpdateTemperatureProfile)}>
              {updateTemperatureProfile.isPending && <LoadingSpinner overlay />}
              <FlexBox>
                <Box
                  display={'flex'}
                  flexDirection={isMobile ? 'column' : 'row'}
                  justifyContent={'right'}
                  alignItems={'center'}
                  pb={4}
                  gap={4}>
                  <Select
                    multiple
                    dark
                    fullWidth
                    checkAll={{
                      value: !!averageTempYears && selectedYears.length === averageTempYears?.length,
                      onChange: () => {
                        if (averageTempYears && averageTempYears.length > 0) {
                          setSelectedYears((prev) => (prev.length !== averageTempYears?.length ? averageTempYears : []));
                        }
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.selectYears` })}
                    value={selectedYears}
                    onChange={(event) => {
                      const years = event.target.value as number[];
                      setSelectedYears(years);
                    }}>
                    {averageTempYears?.map((year) => (
                      <CheckboxSelectItem checked={selectedYears.includes(year)} value={year} key={year}>
                        {year}
                      </CheckboxSelectItem>
                    ))}
                  </Select>

                  <ButtonGroup
                    value={'group'}
                    buttons={[
                      {
                        label: <FormattedMessage id={`${PAGE_NAME_ID}.temperatureDialog.graph`} />,
                        value: 'left',
                        state: false,
                      },
                      {
                        label: <FormattedMessage id={`${PAGE_NAME_ID}.temperatureDialog.list`} />,
                        value: 'right',
                        state: true,
                      },
                    ]}
                    handleClick={(e) => setShowTable(e as boolean)}
                  />
                </Box>

                <>
                  <Typography variant="h4">
                    <FormattedMessage id={`${PAGE_NAME_ID}.temperatureDialog.subTitle`} />
                  </Typography>
                  {!showTable ? (
                    <AverageTemperatureChart data={validData} />
                  ) : (
                    <>
                      {fieldsTemperature.map((field, index) => (
                        <FlexBox key={index}>
                          <TextField
                            dark
                            fullWidth
                            type="number"
                            label={`${intl.formatMessage({ id: 'general.week' })} ${field.weekNumber}`}
                            endAdornment={<>°C</>}
                            controller={{
                              name: `profiles.${index}.averageTemperature`,
                              defaultValue: field.averageTemperature,
                              control: control,
                              rules: {
                                required: intl.formatMessage({ id: 'form.rules.required' }),
                                validate: {
                                  positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                                },
                              },
                            }}
                          />
                        </FlexBox>
                      ))}
                    </>
                  )}
                </>
              </FlexBox>
              <Box display={'flex'} justifyContent={'flex-end'} mt={4}>
                <Button type="submit">
                  <FormattedMessage id="general.save" />
                </Button>
              </Box>
            </form>
          </>
        </Dialog>
      </Box>
      <AverageTemperatureChart data={chartData} />
    </>
  );
};

export default TemperatureProfile;
